import {createComponent, createNgModule, EnvironmentInjector, Injectable, NgModuleRef, Type} from '@angular/core';
import {MatDialogRef} from '@angular/material/dialog';
import {BehaviorSubject, Observable, Subject, take} from 'rxjs';
import {AssertionUtils} from '../../common/utils/assertion-utils';
import {AlertDialogResult} from '../../dialogs/alert-dialog/alert-dialog-data';
import {DialogBuilderFactoryService} from '../../dialogs/dialog-builder/dialog-builder-factory.service';
import {DialogType} from '../../dialogs/dialog-type';
import {ContentEntry} from './content-entry.interface';
import {ContentSwitcherComponentData} from './content-switcher-component-data.interface';
import {ContentComponentInput} from './content-switcher-component-input.interface';
import {ContentSwitcherDialogComponent} from './content-switcher-dialog.component';

@Injectable({providedIn: 'root'})
export class ContentSwitcherDialogService {
  private currentEntry: ContentEntry;
  private contentSwitcherHistory: ContentEntry[] = [];

  private readonly nextComponentRefAndInfo: BehaviorSubject<ContentComponentInput> = new BehaviorSubject(null);

  private dialog: MatDialogRef<ContentSwitcherDialogComponent>;

  public constructor(
    private readonly dialogBuilderFactory: DialogBuilderFactoryService,
    private readonly environmentInjector: EnvironmentInjector
  ) {}

  public clearNavigationHistory(): void {
    this.contentSwitcherHistory = [];
  }

  public navigateForward<T>(componentData: ContentSwitcherComponentData, forgetCurrentEntry: boolean = false): Observable<T> {
    if (AssertionUtils.isNullOrUndefined(this.dialog)) {
      this.dialog = this.openContentSwitcherDialog();
    }

    if (!AssertionUtils.isNullOrUndefined(this.currentEntry) && !forgetCurrentEntry) {
      this.contentSwitcherHistory.push(this.currentEntry);
    }

    if (this.contentSwitcherHistory.length > 100) {
      this.contentSwitcherHistory.shift();
    }

    this.currentEntry = {
      componentData,
      navigateBackData: new Subject<T>(),
      componentRef: createComponent(componentData.component, {
        environmentInjector: this.getInjector(componentData.module)
      })
    };

    Object.assign(this.currentEntry.componentRef.instance, this.currentEntry.componentData.componentInputs);

    this.nextComponentRefAndInfo.next({componentData, componentRef: this.currentEntry.componentRef, destroyPrevious: false});

    return this.currentEntry.navigateBackData.asObservable().pipe(take(1));
  }

  public navigateBack<T>(data: T): void {
    if (this.arePagesValid(true)) {
      this.returnOnePage(data);
    } else {
      this.openDiscardDialog(false);
    }
  }

  public resetContentSwitcher(): number {
    this.currentEntry.componentRef.destroy();
    this.currentEntry.navigateBackData.complete();
    this.currentEntry = undefined;

    const dialogHistorySize = this.contentSwitcherHistory.length;

    this.contentSwitcherHistory.forEach((contentEntry: ContentEntry): void => {
      contentEntry.componentRef.destroy();
      contentEntry.navigateBackData.complete();
    });
    this.contentSwitcherHistory = [];

    this.closeContentSwitcherDialog();

    return dialogHistorySize;
  }

  public closeDialogButMaintainHistory(): void {
    if (!AssertionUtils.isNullOrUndefined(this.currentEntry)) {
      this.contentSwitcherHistory.push(this.currentEntry);
    }

    this.closeContentSwitcherDialog();
  }

  private returnOnePage<T>(data: T): void {
    if (!AssertionUtils.isNullOrUndefined(this.currentEntry)) {
      this.currentEntry.navigateBackData.next(data);

      this.currentEntry.navigateBackData.complete();
    }

    this.currentEntry = this.contentSwitcherHistory.pop();

    if (!AssertionUtils.isNullOrUndefined(this.currentEntry)) {
      if (AssertionUtils.isNullOrUndefined(this.dialog)) {
        this.dialog = this.openContentSwitcherDialog();
      }

      this.nextComponentRefAndInfo.next({componentData: this.currentEntry.componentData, componentRef: this.currentEntry.componentRef, destroyPrevious: true});
    } else if (!AssertionUtils.isNullOrUndefined(this.dialog)) {
      this.closeContentSwitcherDialog();
    }
  }

  private arePagesValid(onlyCheckCurrentEntry: boolean = false): boolean {
    let arePagesValid = true;

    if (!AssertionUtils.isNullOrUndefined(this.currentEntry?.componentRef?.instance?.canLeavePage?.())) {
      arePagesValid = this.currentEntry.componentRef.instance?.canLeavePage?.();
    }

    if (onlyCheckCurrentEntry) {
      return arePagesValid;
    }

    this.contentSwitcherHistory.forEach((entry: ContentEntry) => {
      const isValid = entry.componentRef.instance?.canLeavePage?.();
      if (isValid === false) {
        arePagesValid = false;
      }
    });

    return arePagesValid;
  }

  private checkIfCanCloseDialog(closeDialog: boolean): void {
    if (!this.arePagesValid()) {
      this.openDiscardDialog(closeDialog);
    } else {
      this.resetContentSwitcher();
    }
  }

  private openDiscardDialog(closeDialog: boolean): void {
    this.dialogBuilderFactory
      .getBuilder()
      .openAlertDialog({
        type: DialogType.LEAVE_AND_CONTINUE
      })
      .subscribe((result: AlertDialogResult) => {
        if (result === AlertDialogResult.CANCEL) {
          if (closeDialog) {
            this.resetContentSwitcher();
          } else {
            this.returnOnePage(undefined);
          }
        }
      });
  }

  private openContentSwitcherDialog(): MatDialogRef<ContentSwitcherDialogComponent> {
    const dialogRef = this.dialogBuilderFactory.getBuilder().withWidth('80vw').withMinWidth(900).withHeight('80vh').withCloseDisabled().withMinHeight(480).open(
      ContentSwitcherDialogComponent,
      {
        nextComponentRefAndInfo: this.nextComponentRefAndInfo
      },
      true
    );

    dialogRef.backdropClick().subscribe(() => {
      this.checkIfCanCloseDialog(true);
    });

    return dialogRef;
  }

  private getInjector(module?: Type<any>): EnvironmentInjector {
    let parentInjector = this.environmentInjector;
    if (!AssertionUtils.isNullOrUndefined(this.currentEntry?.componentRef)) {
      parentInjector = this.currentEntry.componentRef.injector.get(NgModuleRef).injector;
    }

    if (!AssertionUtils.isNullOrUndefined(module)) {
      const moduleRef = createNgModule(module, parentInjector);
      return moduleRef.injector;
    } else {
      return parentInjector;
    }
  }

  private closeContentSwitcherDialog(): undefined {
    this.dialog?.close();
    this.dialog = undefined;
  }
}
